import { HttpInterceptorFn, HttpBackend, HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, finalize, switchMap, take, throwError, of, Observable, tap, EMPTY, race } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment.prod';

let isRefreshing = false;
let hasRedirectedToLogin = false;
const accessToken$ = new BehaviorSubject<string | null>(localStorage.getItem('@administrator'));
const refreshFailed$ = new BehaviorSubject<boolean>(false);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const modalService = inject(NgbModal);
  const router = inject(Router);

  // Bypass HttpClient (does NOT go through interceptors)
  const httpBackend = inject(HttpBackend);
  const bypassHttp = new HttpClient(httpBackend);

  const token = localStorage.getItem('@administrator');

  function decodeBase64Url(input: string): string {
    // JWT uses base64url (not plain base64)
    const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
    return atob(padded);
  }

  function isTokenExpired(t: string): boolean {
    try {
      const part = t.split('.')[1];
      if (!part) return false; // can't parse -> don't force refresh; rely on 401 handler
      const payload = JSON.parse(decodeBase64Url(part));
      const exp = typeof payload?.exp === 'number' ? payload.exp : null; // seconds
      if (!exp) return false;

      // 30s skew so we refresh slightly before expiry (avoids edge 401s)
      const nowSec = Math.floor(Date.now() / 1000);
      return exp <= (nowSec + 30);
    } catch {
      // If parsing fails, do NOT assume expired; rely on 401 path to refresh.
      return false;
    }
  }

  function logoutAndRedirect() {
    // Prevent duplicate redirects / repeated side effects
    if (hasRedirectedToLogin) return;

    try { modalService.dismissAll(); } catch {}
    localStorage.removeItem('@administrator');
    localStorage.removeItem('@refresh_token');

    // Release any queued requests
    accessToken$.next(null);
    refreshFailed$.next(true);
    isRefreshing = false;

    hasRedirectedToLogin = true;
    router.navigate(['login']);
  }

  function isInvalidRefreshError(err: any): boolean {
    const msg = err?.error?.error || err?.error?.message || err?.message;
    // Backend returns: { error: "Refresh token invalid" }
    if (typeof msg === 'string' && msg.toLowerCase().includes('refresh token invalid')) return true;
    // Also treat 401/400 from refresh endpoint as invalid refresh.
    return err?.status === 401 || err?.status === 400;
  }

  function doRefresh(): Observable<string> {
    const refreshToken = localStorage.getItem('@refresh_token');
    if (!refreshToken) {
      return throwError(() => new Error('Refresh token missing'));
    }

    // IMPORTANT: refresh request must bypass interceptors to avoid DI cycles
    return bypassHttp
      .post<any>(environment.apiUrl + 'admin/refresh', { refresh_token: refreshToken }, { withCredentials: true })
      .pipe(
        switchMap((res: any) => {
          const payload = res?.data ?? res;
          const newToken = payload?.access_token;
          const newRefresh = payload?.refresh_token;

          if (!newToken) {
            return throwError(() => new Error('No new token in refresh response'));
          }

          localStorage.setItem('@administrator', newToken);
          if (newRefresh) localStorage.setItem('@refresh_token', newRefresh);
          accessToken$.next(newToken);
          refreshFailed$.next(false);
          hasRedirectedToLogin = false;
          return of(newToken);
        }),
        catchError((err) => {
          // If refresh token is invalid/expired -> force logout
          if (isInvalidRefreshError(err)) {
            console.error('[Interceptor] Refresh token invalid/expired. Logging out.', err);
            logoutAndRedirect();
          }
          return throwError(() => err);
        })
      );
  }

  function waitForTokenAndRetry(originalReq: any) {
    const tokenReady$ = accessToken$.pipe(
      filter((t): t is string => !!t),
      take(1),
      switchMap((t) => {
        const retryReq = originalReq.clone({
          setHeaders: { Authorization: `Bearer ${t}` },
          withCredentials: true
        });
        return next(retryReq);
      })
    );

    const failed$ = refreshFailed$.pipe(
      filter((failed) => failed === true),
      take(1),
      switchMap(() => {
        // Ensure we redirect immediately and stop pending requests
        logoutAndRedirect();
        return EMPTY;
      })
    );

    // Whichever happens first: token arrives or refresh fails.
    return race(tokenReady$, failed$);
  }

  let authReq = req;

  // Only handle auth/refresh for admin API calls
  const isAdminApiCall = req.url.includes('/admin/');
  const isRefreshCall = req.url.includes('/admin/refresh');
  const isLoginCall = req.url.includes('/admin/login');

  // Non-admin requests should pass through untouched
  if (!isAdminApiCall) {
    return next(req);
  }

  if (token) {
    const expired = isTokenExpired(token);
    console.log(`[Interceptor] Token found. Expired: ${expired}. url=${req.url}`);

    if (expired && !isRefreshCall) {
      // Avoid flooding the API with expired tokens (prevents 401/429 bursts)
      if (isRefreshing) {
        console.log('[Interceptor] Refresh in progress. Waiting for new token...');
        return waitForTokenAndRetry(req);
      }

      console.warn('[Interceptor] Token expired. Refreshing before request.');
      isRefreshing = true;
      accessToken$.next(null);
      refreshFailed$.next(false);

      return doRefresh().pipe(
        switchMap((newToken) => {
          const retryReq = req.clone({
            setHeaders: { Authorization: `Bearer ${newToken}` },
            withCredentials: true
          });
          console.log('[Interceptor] Proceeding with request after refresh to:', retryReq.url);

          // If the retried request fails (500/404/etc), do NOT logout.
          return next(retryReq).pipe(
            catchError((reqErr) => {
              return throwError(() => reqErr);
            })
          );
        }),
        catchError((err) => {
          // Logout ONLY if refresh itself failed / unauthorized.
          if (err?.url?.includes('/admin/refresh') || isInvalidRefreshError(err)) {
            console.error('[Interceptor] Refresh failed (pre-request). Logging out.', err);
            logoutAndRedirect();
          } else {
            console.error('[Interceptor] Request failed after refresh (no logout).', err);
          }
          return throwError(() => err);
        }),
        finalize(() => {
          isRefreshing = false;
        })
      );
    }

    // token is not expired (or is refresh call) -> attach token and proceed
    authReq = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
      withCredentials: true
    });
  } else {
    console.log('[Interceptor] No token found.');

    // If this is an admin API call (not login/refresh) and we have no token,
    // do NOT proceed (prevents request spam + 429). Redirect to login once.
    if (isAdminApiCall && !isRefreshCall && !isLoginCall) {
      if (!hasRedirectedToLogin) {
        hasRedirectedToLogin = true;
        try { modalService.dismissAll(); } catch {}
        router.navigate(['login']);
      }
      return EMPTY; // ✅ არ “კრაშდება” აპი
    }
  }

  console.log('[Interceptor] Proceeding with request to:', authReq.url);

  return next(authReq).pipe(
    catchError((error) => {
      // ✅ rate limit ან server error - არ არის auth პრობლემა
      if (error?.status === 429) {
        console.warn('[Interceptor] Rate limited (429).');
        return throwError(() => error);
      }

      if (error?.status >= 500 && error?.status <= 599) {
        console.warn('[Interceptor] Server error (5xx).');
        return throwError(() => error);
      }
      if (error.status === 401 && isAdminApiCall && !isLoginCall && !isRefreshCall) {
        if (req.url.includes('/admin/refresh')) {
          // If refresh itself is unauthorized, force logout
          console.error('[Interceptor] Refresh call unauthorized. Logging out.');
          logoutAndRedirect();
          return throwError(() => error);
        }

        // If a refresh is already happening, wait for it and then retry once.
        if (isRefreshing) {
          return waitForTokenAndRetry(req);
        }

        console.warn('[Interceptor] Unauthorized response received. Trying refresh.');
        isRefreshing = true;
        accessToken$.next(null);
        refreshFailed$.next(false);

        return doRefresh().pipe(
          switchMap((newToken) => {
            const retryReq = req.clone({
              setHeaders: { Authorization: `Bearer ${newToken}` },
              withCredentials: true
            });

            // If the retried request fails, do NOT logout.
            return next(retryReq).pipe(
              catchError((reqErr) => throwError(() => reqErr))
            );
          }),
          catchError((err) => {
            // Only logout when refresh itself failed.
            if (err?.url?.includes('/admin/refresh') || isInvalidRefreshError(err)) {
              console.error('[Interceptor] Refresh token failed (401 path). Logging out.', err);
              logoutAndRedirect();
            } else {
              console.error('[Interceptor] Request failed after refresh (401 path, no logout).', err);
            }
            return throwError(() => err);
          }),
          finalize(() => {
            isRefreshing = false;
          })
        );
      }

      // If server rate limits, do not retry here (avoid loops)
      if (error.status === 429) {
        console.warn('[Interceptor] Rate limited (429).');
        return throwError(() => error);
      }

      return throwError(() => error);
    })
  );
};